/**
 * LoRA Training service — types + MOCK data for the admin LoRA Training UI.
 *
 * First cut is mock-only so the UI is reviewable before the backend lands.
 * When `/admin/loras` exists, swap the mock bodies for `request<T>()` calls
 * (see ./client.ts + ./gpu-hosts.ts for the pattern) — the exported types +
 * function signatures stay the same, so the pages don't change.
 *
 * Pipeline mirrors the proven manual flow (~/gina-gold): IL body + face-inpaint
 * (klein-9B) → curate → train on FLUX.2 klein-9B (ai-toolkit, A100 cuda:3).
 */
export type LoraKind = 'character' | 'style' | 'object';
export type LoraStatus = 'draft' | 'generating' | 'curating' | 'training' | 'ready' | 'failed';
export type FrameGroup = 'face' | 'body' | 'variety';

export interface Lora {
  id: string;
  name: string;
  codename?: string;
  kind: LoraKind;
  status: LoraStatus;
  base: string;
  trigger?: string;
  images: number;
  /** 0-100 while training. */
  progress?: number;
  step?: number;
  totalSteps?: number;
  eta?: string;
  updatedAt: string;
}

/** One generated candidate image in the curate grid. */
export interface Frame {
  id: string;
  group: FrameGroup;
  label: string;
  selected: boolean;
}

const MOCK_LORAS: Lora[] = [
  {
    id: 'lin',
    name: 'LIN',
    codename: 'gina',
    kind: 'character',
    status: 'training',
    base: 'FLUX.2 klein-9B',
    trigger: 'ginalin',
    images: 44,
    progress: 31,
    step: 940,
    totalSteps: 3000,
    eta: '~2.1 hrs',
    updatedAt: 'training now',
  },
  { id: 'maya', name: 'MAYA', kind: 'character', status: 'ready', base: 'FLUX.2 klein-9B', trigger: 'mayachar', images: 38, updatedAt: '3 days ago' },
  { id: 'tang', name: 'TANG', kind: 'character', status: 'ready', base: 'FLUX.2 klein-9B', trigger: 'tangchar', images: 41, updatedAt: '1 week ago' },
  { id: 'noa', name: 'NOA', kind: 'character', status: 'draft', base: 'FLUX.2 klein-9B', images: 0, updatedAt: '2 days ago' },
];

export async function listLoras(kind: LoraKind = 'character'): Promise<Lora[]> {
  await new Promise((r) => setTimeout(r, 120));
  return MOCK_LORAS.filter((l) => l.kind === kind);
}

/** Mock generated training set for the curate step (24 face · 12 body · 8 variety, sampled). */
export function mockFrames(): Frame[] {
  const f: Frame[] = [];
  const faces = ['smile · soft', '3/4 left', 'serious · dramatic', 'profile', 'window light', 'golden 3/4', 'laugh · day', 'neutral · rim'];
  const bodies = ['standing front', 'profile', 'butterfly', 'lean-back V', 'kneeling', 'all-fours', 'supine', 'side-sit'];
  const variety = ['golden garden', 'cool window', 'dramatic dark', 'overcast field', 'warm interior', 'sunset rim', 'blue-hour pool', 'bright terrace'];
  faces.forEach((l, i) => f.push({ id: `face-${i}`, group: 'face', label: l, selected: true }));
  bodies.forEach((l, i) => f.push({ id: `body-${i}`, group: 'body', label: l, selected: i !== 4 }));
  variety.forEach((l, i) => f.push({ id: `var-${i}`, group: 'variety', label: l, selected: true }));
  return f;
}
